import React, { createContext, useCallback, useContext, useState } from 'react';
import _isArray from 'lodash/isArray';
import _get from 'lodash/get';

import { PageErrorUi } from 'components';
import {
  useForm,
  useMutation,
  useParallelFetch,
  useSegment
} from 'utils/hooks';
import { createFormData, createPayload } from './utils';
import { DEFAULT_FORM_DATA, FORM_SCHEMA } from './stubs';
import { IS_ALL_SSR } from 'dictionary';

/**
 * @type {React.Context<{
 *  data: import("zod").z.infer<typeof import('./stubs').FORM_SCHEMA>,
 *  rawData: { preferredCarrier: any, carriers: any[] },
 *  errors: Partial<import("zod").z.infer<typeof import('./stubs').FORM_SCHEMA>>,
 *  slaOptions: {label: string, value: string, options: {label: string, value: string, carrierId: string, carrierName: string}[]}[],
 *  isDirty: boolean,
 *  isLoading: boolean,
 *  isProcessing: boolean,
 *  onValidate: (updates: Partial<import("zod").z.infer<typeof import('./stubs').FORM_SCHEMA>>) => void,
 *  onChange: (updates: Partial<import("zod").z.infer<typeof import('./stubs').FORM_SCHEMA>>) => void,
 *  onSave: () => Promise<any>
 * }>}
 */
const PreferredCarrierContext = createContext();

export function usePreferredCarrier() {
  const context = useContext(PreferredCarrierContext);
  if (!context) {
    throw new Error(
      'usePreferredCarrier must be used within a PreferredCarrierProvider'
    );
  }
  return context;
}

export function PreferredCarrierProvider({ children = null }) {
  const { track } = useSegment();

  const [slaOptions, setSlaOptions] = useState([]);

  const {
    data,
    errors,
    isDirty,
    onChange,
    onValidate,
    onSetInitialData
  } = useForm({
    dataSchema: FORM_SCHEMA,
    defaultData: DEFAULT_FORM_DATA
  });

  const {
    data: rawData,
    isError,
    isLoading,
    fetchData: onReload
  } = useParallelFetch(
    [
      {
        dataKey: 'carriers',
        apiKey: 'configure.carriers.root',
        onTransform: data => (_isArray(data) ? data : [])
      },
      {
        dataKey: 'preferredCarrier',
        apiKey: 'configure.carriers.preferred'
      }
    ],
    {
      loadOnMount: !IS_ALL_SSR,
      initialData: { carriers: [], preferredCarrier: {} },
      onSuccess: data => {
        const dataset = createFormData(data);
        onSetInitialData(dataset.formData);
        setSlaOptions(dataset.slaOptions);
      }
    }
  );

  const { isProcessing, mutate } = useMutation(
    'configure.carriers.preferred',
    'PUT',
    {
      errorMessage: 'Failed to update preferred carrier settings.',
      payload: () => createPayload(data),
      onValidate: () => onValidate(data),
      onSuccess: () => {
        track('FTU Wizard Preferred Carrier Configured', {
          is_enabled: data.is_enabled,
          carrier: data.is_enabled ? _get(data.sla, 'carrierName', null) : null,
          sla: data.is_enabled ? _get(data.sla, 'label', null) : null,
          mode: data.is_enabled ? 'specific' : 'cheapest'
        });
        onSetInitialData(data);
      }
    }
  );

  const onSave = useCallback(() => {
    if (IS_ALL_SSR) return;
    return new Promise((resolve, reject) => {
      mutate({}, (err, data) => {
        if (err) reject(err);
        else resolve(data);
      });
    });
  }, [mutate]);

  return (
    <PreferredCarrierContext.Provider
      value={{
        data,
        rawData,
        errors,
        slaOptions,
        isDirty,
        isLoading,
        isProcessing,
        onValidate,
        onChange,
        onSave
      }}
    >
      <PageErrorUi isError={isError} onReload={onReload}>
        {children}
      </PageErrorUi>
    </PreferredCarrierContext.Provider>
  );
}
